import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { SharedModule } from '../../shared/shared.module';
import { LabelService } from '../../services/label.service';
import {
  LabelAndDescription,
  LabelAndSelected,
  PatientPharmacotherapy,
} from '../../models/patient-model';
import { activePrinciples } from '../../models/hardcoded-data';
import { ErrorPanelService } from '../../services/error-panel.service';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationPanelComponent } from '../../shared/confirmation-panel/confirmation-panel.component';
import { PatientPharmacoterapiesService } from '../../services/patient-pharmacotherapies.service';
import { PatientPharmacotherapyInput } from '../../models/patient-pharmacotherapy-model';

@Component({
  selector: 'app-pharmacotherapy',
  standalone: true,
  imports: [
    FormsModule,
    SharedModule,
    MatIconModule,
    MatMenuModule,
    MatTableModule,
    MatTooltipModule,
  ],
  templateUrl: './pharmacotherapy.component.html',
  styleUrl: './pharmacotherapy.component.scss',
})
export class PharmacotherapyComponent {
  @Input() patientId: string = '';
  @Input() pharmacotherapies: PatientPharmacotherapy[] = [];
  @Input() hasModifyButtonBeenClicked: boolean = false;
  @Input() showActivePrinciplesPanel: boolean = false;
  @Input() isLoading: boolean = false;
  @Output() reloadPharmacotherapies = new EventEmitter<void>();
  @Output() toggleActivePrinciples = new EventEmitter<void>();

  displayedColumns: string[] = ['active_principles', 'description', 'actions'];

  // Active principles management
  activePrinciplesList: string[] = activePrinciples;
  newActivePrinciplesObj: LabelAndSelected[] =
    this.createActivePrinciplesObjects();
  editActivePrinciplesObj: LabelAndSelected[] = [];

  // Form data
  selectedActivePrinciples: string = '';
  pharmacotherapyDescription: string = '';

  // UI state
  showModifyPharmacotherapyPanel: boolean = false;
  showDeletePanel: boolean = false;
  inputBoxesForModifyPharmacotherapy: LabelAndDescription[] = [];

  // Selected pharmacotherapy for editing
  selectedPharmacotherapy: PatientPharmacotherapy = {
    id: '',
    patient_id: '',
    active_principle: '',
    description: '',
  };

  constructor(
    public labelService: LabelService,
    public errorPanelService: ErrorPanelService,
    private dialog: MatDialog,
    private patientPharmacoterapiesService: PatientPharmacoterapiesService
  ) {}

  private createActivePrinciplesObjects(): LabelAndSelected[] {
    return this.activePrinciplesList.map((principle) => ({
      label: principle,
      selected: false,
    }));
  }

  handleMenuAction(
    action: string,
    pharmacotherapy: PatientPharmacotherapy
  ): void {
    switch (action) {
      case 'edit':
        this.toggleModifyPanel(pharmacotherapy);
        break;
      case 'delete':
        this.deletePharmacotherapy(pharmacotherapy);
        break;
    }
  }

  toggleModifyPanel(pharmacotherapy?: PatientPharmacotherapy): void {
    if (pharmacotherapy) {
      this.selectedPharmacotherapy = pharmacotherapy;
      this.pharmacotherapyDescription = pharmacotherapy.description;
      this.editActivePrinciplesObj = this.createEditActivePrinciplesObjects(
        pharmacotherapy.active_principle
      );
    }
    this.showModifyPharmacotherapyPanel = !this.showModifyPharmacotherapyPanel;
  }

  private createEditActivePrinciplesObjects(
    activePrinciple: string
  ): LabelAndSelected[] {
    const principles = activePrinciple.split(',');
    return this.activePrinciplesList.map((principle) => ({
      label: principle,
      selected: principles.includes(principle),
    }));
  }

  confirmModifyPharmacotherapy(event: string[]): void {
    this.patientPharmacoterapiesService
      .updatePatientPharmacoterapies(
        {
          patient_id: this.patientId,
          active_principle: event[0],
          description: event[1],
        },
        this.selectedPharmacotherapy.id
      )
      .subscribe({
        next: (response: PatientPharmacotherapy) => {
          console.log('Farmacoterapia aggiornata');
          this.toggleModifyPanel();
          this.reloadPharmacotherapies.emit();
        },
        error: (error: any) => {
          console.error("Errore nell'aggiornare la farmacoterapia", error);
          this.toggleModifyPanel();
          this.errorPanelService.showErrorSnackBar(
            "Errore nell'aggiornare la farmacoterapia"
          );
        },
      });
  }

  deletePharmacotherapy(pharmacotherapy: PatientPharmacotherapy): void {
    const dialogRef = this.dialog.open(ConfirmationPanelComponent, {
      data: {
        title: 'Sei sicuro di voler eliminare questa farmacoterapia?',
        infoText: "L'operazione è irreversibile",
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.performDelete(pharmacotherapy);
      }
    });
  }

  private performDelete(pharmacotherapy: PatientPharmacotherapy): void {
    this.patientPharmacoterapiesService
      .deletePatientPharmacoterapies(pharmacotherapy.id)
      .subscribe({
        next: () => {
          this.pharmacotherapies = this.pharmacotherapies.filter(
            (pharma) => pharma.id !== pharmacotherapy.id
          );
        },
        error: (error: any) => {
          console.error("Errore nell'eliminare la farmacoterapia", error);
          this.errorPanelService.showErrorSnackBar(
            "Errore nell'eliminare la farmacoterapia"
          );
        },
      });
  }

  confirmNewPharmacotherapy(event: string[]): void {
    const requestBody: PatientPharmacotherapyInput = {
      patient_id: this.patientId,
      active_principle: event[0],
      description: event[1],
    };

    this.patientPharmacoterapiesService
      .createPatientPharmacoterapies(requestBody)
      .subscribe({
        next: (response: PatientPharmacotherapy) => {
          console.log('Farmacoterapia aggiunta');
          this.pharmacotherapies.push(response);
          this.reloadPharmacotherapies.emit();
          this.toggleActivePrinciplesPanel();
        },
        error: (error: any) => {
          console.error("Errore nell'aggiunta della farmacoterapia", error);
          this.errorPanelService.showErrorSnackBar(
            "Errore nell'aggiunta della farmacoterapia"
          );
        },
      });
  }

  toggleActivePrinciplesPanel(): void {
    this.resetActivePrinciplesSelection();
    this.toggleActivePrinciples.emit();
  }

  private resetActivePrinciplesSelection(): void {
    this.newActivePrinciplesObj.forEach((obj) => (obj.selected = false));
  }
}
